import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import './AdminDashboard.css';

// Helper function to format payment method display
const formatPaymentMethod = (type, brand) => {
    const methodLabels = {
        'apple_pay': 'Apple Pay',
        'google_pay': 'Google Pay',
        'link': 'Link',
        'card': 'Card',
    };

    const brandLabels = {
        'visa': 'Visa',
        'mastercard': 'Mastercard',
        'amex': 'Amex',
        'discover': 'Discover',
        'diners': 'Diners',
        'jcb': 'JCB',
        'unionpay': 'UnionPay',
    };

    const methodLabel = methodLabels[type] || type || 'Card';
    const brandLabel = brandLabels[brand] || '';

    if (type === 'apple_pay' || type === 'google_pay' || type === 'link') {
        return methodLabel;
    }

    return brandLabel ? `${brandLabel}` : methodLabel;
};

// Helper function to format action type for display
const formatActionType = (type) => {
    const typeMap = {
        'login': { label: 'Login', color: '#28a745' },
        'login_failed': { label: 'Failed Login', color: '#dc3545' },
        'logout': { label: 'Logout', color: '#6c757d' },
        'session_expired': { label: 'Session Expired', color: '#ffc107' },
        'tile_verify': { label: 'Verify Tile', color: '#28a745' },
        'tile_reject': { label: 'Reject Tile', color: '#dc3545' },
        'tile_update': { label: 'Update Tile', color: '#17a2b8' },
        'tile_delete': { label: 'Delete Tile', color: '#dc3545' },
        'tile_add_reserved': { label: 'Add Reserved', color: '#007bff' },
        'tile_inquiry': { label: 'New Inquiry', color: '#17a2b8' },
        'inquiry_confirmed': { label: 'Inquiry Confirmed', color: '#28a745' },
        'inquiry_contacted': { label: 'Inquiry Contacted', color: '#17a2b8' },
        'inquiry_cancelled': { label: 'Inquiry Cancelled', color: '#dc3545' },
        'inquiry_delete': { label: 'Inquiry Deleted', color: '#dc3545' },
    };
    return typeMap[type] || { label: type || 'Action', color: '#6c757d' };
};

// Helper function to format payment plan for display
const formatPaymentPlan = (plan) => {
    const planMap = {
        'full': 'Full Payment',
        '3': '3 Monthly Instalments',
        '6': '6 Monthly Instalments',
        '12': '12 Monthly Instalments',
    };
    return planMap[plan] || plan;
};

// Helper function to format inquiry status
const formatInquiryStatus = (status) => {
    const statusMap = {
        'pending': { label: 'New Inquiry', color: '#ffc107', bgColor: '#fff3cd' },
        'contacted': { label: 'Contacted', color: '#17a2b8', bgColor: '#d1ecf1' },
        'confirmed': { label: 'Confirmed', color: '#28a745', bgColor: '#d4edda' },
        'cancelled': { label: 'Cancelled', color: '#dc3545', bgColor: '#f8d7da' },
    };
    return statusMap[status] || { label: status, color: '#6c757d', bgColor: '#e2e3e5' };
};

const RESERVED_TILES_COUNT = 75; // First 3 rows (25 tiles per row)
const SESSION_TIMEOUT = 5 * 60 * 1000; // 5 minutes in milliseconds

const AdminDashboard = () => {
    const { user, logout, isAuthenticated, isLoading: authLoading } = useAuth0();
    const [activeTab, setActiveTab] = useState('tiles');
    // Commented out - not currently used
    // const [services, setServices] = useState([]);
    // const [events, setEvents] = useState([]);
    // const [blogs, setBlogs] = useState([]);
    // const [volunteers, setVolunteers] = useState([]);
    const [tileReservations, setTileReservations] = useState([]);
    const [inquiries, setInquiries] = useState([]);
    const [auditLogs, setAuditLogs] = useState([]);
    const [adminUsers, setAdminUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showReservedForm, setShowReservedForm] = useState(false);
    const [reservedTileNumber, setReservedTileNumber] = useState('');
    const [reservedDonorName, setReservedDonorName] = useState('');
    const [reservedDonorEmail, setReservedDonorEmail] = useState('');
    const [reservedDonorPhone, setReservedDonorPhone] = useState('');
    const [reservedFormError, setReservedFormError] = useState('');
    const [sessionTimeLeft, setSessionTimeLeft] = useState(SESSION_TIMEOUT);
    const [lastActivity, setLastActivity] = useState(Date.now());
    const navigate = useNavigate();

    // Session timeout handler
    const handleLogout = useCallback(async (isTimeout = false) => {
        // Use Auth0 logout
        logout({
            logoutParams: {
                returnTo: window.location.origin + '/admin' + (isTimeout ? '?timeout=true' : '')
            }
        });
    }, [logout]);

    // Auth0 authentication check and data loading
    useEffect(() => {
        if (authLoading) return;

        if (!isAuthenticated) {
            navigate('/admin');
            return;
        }

        fetchData();

        // Activity tracking - debounced for session timeout display
        let activityTimeout;
        const resetActivity = () => {
            clearTimeout(activityTimeout);
            activityTimeout = setTimeout(() => {
                setLastActivity(Date.now());
            }, 1000); // Debounce 1 second
        };

        // Track user activity
        window.addEventListener('mousemove', resetActivity);
        window.addEventListener('keypress', resetActivity);
        window.addEventListener('click', resetActivity);
        window.addEventListener('scroll', resetActivity);

        return () => {
            clearTimeout(activityTimeout);
            window.removeEventListener('mousemove', resetActivity);
            window.removeEventListener('keypress', resetActivity);
            window.removeEventListener('click', resetActivity);
            window.removeEventListener('scroll', resetActivity);
        };
    }, [navigate, isAuthenticated, authLoading]);

    // Session timeout countdown
    useEffect(() => {
        const interval = setInterval(() => {
            const elapsed = Date.now() - lastActivity;
            const remaining = SESSION_TIMEOUT - elapsed;

            if (remaining <= 0) {
                handleLogout(true);
            } else {
                setSessionTimeLeft(remaining);
            }
        }, 1000);

        return () => clearInterval(interval);
    }, [lastActivity, handleLogout]);

    const fetchData = async () => {
        try {
            const [tilesRes, auditRes, inquiriesRes, usersRes] = await Promise.all([
                fetch('/api/wall-tiles/admin'),
                fetch('/api/audit?limit=100'),
                fetch('/api/inquiries'),
                fetch('/api/admin/users').catch(() => ({ ok: false }))
            ]);

            const tilesData = await tilesRes.json();
            const auditData = await auditRes.json();
            const inquiriesData = await inquiriesRes.json();
            const usersData = usersRes.ok ? await usersRes.json() : [];

            setTileReservations(Array.isArray(tilesData) ? tilesData : []);
            setAuditLogs(auditData?.logs || []);
            setInquiries(Array.isArray(inquiriesData) ? inquiriesData : []);
            setAdminUsers(Array.isArray(usersData) ? usersData : []);
        } catch (error) {
            console.error('Error fetching data:', error);
            setTileReservations([]);
            setAuditLogs([]);
            setInquiries([]);
            setAdminUsers([]);
        } finally {
            setLoading(false);
        }
    };

    const updateTileStatus = async (tileNumber, status) => {
        try {
            await fetch('/api/wall-tiles/admin', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ tile_number: tileNumber, status })
            });
            fetchData();
        } catch (error) {
            alert('Error updating tile status');
        }
    };

    const deleteTileReservation = async (tileNumber) => {
        if (!window.confirm('Are you sure you want to delete this tile reservation?')) return;

        try {
            await fetch(`/api/wall-tiles/admin?tile_number=${tileNumber}`, { method: 'DELETE' });
            fetchData();
        } catch (error) {
            alert('Error deleting tile reservation');
        }
    };

    const handleAddReservedTile = async (e) => {
        e.preventDefault();
        setReservedFormError('');

        const tileNum = parseInt(reservedTileNumber);
        if (isNaN(tileNum) || tileNum < 1 || tileNum > RESERVED_TILES_COUNT) {
            setReservedFormError(`Tile number must be between 1 and ${RESERVED_TILES_COUNT}`);
            return;
        }

        // Check if tile is already taken
        const existingTile = tileReservations.find(t => t.tile_number === tileNum);
        if (existingTile) {
            setReservedFormError(`Tile #${tileNum} is already assigned to ${existingTile.donor_name}`);
            return;
        }

        try {
            const response = await fetch('/api/wall-tiles/admin/reserved', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    tile_number: tileNum,
                    donor_name: reservedDonorName.trim(),
                    donor_email: reservedDonorEmail.trim(),
                    donor_phone: reservedDonorPhone.trim()
                })
            });

            if (response.ok) {
                setShowReservedForm(false);
                setReservedTileNumber('');
                setReservedDonorName('');
                setReservedDonorEmail('');
                setReservedDonorPhone('');
                fetchData();
            } else {
                const data = await response.json();
                setReservedFormError(data.error || 'Failed to add reserved tile');
            }
        } catch (error) {
            setReservedFormError('Error adding reserved tile');
        }
    };

    const updateInquiryStatus = async (inquiryId, status, notes = null) => {
        try {
            const body = { inquiry_id: inquiryId, status };
            if (notes !== null) body.admin_notes = notes;

            await fetch('/api/inquiries', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body)
            });
            fetchData();
        } catch (error) {
            alert('Error updating inquiry status');
        }
    };

    const deleteInquiry = async (inquiryId) => {
        if (!window.confirm('Are you sure you want to delete this inquiry? This will also release the reserved tiles.')) return;

        try {
            await fetch(`/api/inquiries?id=${inquiryId}`, { method: 'DELETE' });
            fetchData();
        } catch (error) {
            alert('Error deleting inquiry');
        }
    };

    const updateUserApproval = async (userId, action) => {
        const actionText = action === 'approve' ? 'approve' : 'reject';
        if (!window.confirm(`Are you sure you want to ${actionText} this user?`)) return;

        try {
            await fetch('/api/admin/users', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    user_id: userId,
                    action: action,
                    approved_by: user?.email || 'admin'
                })
            });
            fetchData();
        } catch (error) {
            alert(`Error ${actionText}ing user`);
        }
    };

    const transferPrimaryAdmin = async (userId, targetEmail) => {
        if (!window.confirm(`Are you sure you want to transfer Primary Admin status to ${targetEmail}? You will no longer be the primary admin.`)) return;

        try {
            const response = await fetch('/api/admin/users', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    user_id: userId,
                    action: 'transfer_primary',
                    requester_email: user?.email
                })
            });
            const data = await response.json();
            if (response.ok) {
                alert(data.message || 'Primary admin transferred successfully');
                fetchData();
            } else {
                alert(data.error || 'Failed to transfer primary admin');
            }
        } catch (error) {
            alert('Error transferring primary admin');
        }
    };

    const deleteUser = async (userId, targetEmail) => {
        if (!window.confirm(`Are you sure you want to permanently delete ${targetEmail}? This action cannot be undone.`)) return;

        try {
            const response = await fetch(`/api/admin/users?user_id=${userId}&requester_email=${encodeURIComponent(user?.email)}`, {
                method: 'DELETE'
            });
            const data = await response.json();
            if (response.ok) {
                alert(data.message || 'User deleted successfully');
                fetchData();
            } else {
                alert(data.error || 'Failed to delete user');
            }
        } catch (error) {
            alert('Error deleting user');
        }
    };

    // Check if current user is primary admin
    const currentUserIsPrimary = adminUsers.find(u => u.email === user?.email)?.is_primary_admin;

    const formatTimeLeft = (ms) => {
        const minutes = Math.floor(ms / 60000);
        const seconds = Math.floor((ms % 60000) / 1000);
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    };

    if (loading) {
        return <div className="admin-loading">Loading...</div>;
    }

    return (
        <div className="admin-dashboard">
            <header className="admin-header">
                <h1>PCIC Admin Dashboard</h1>
                <div className="header-right">
                    {user && (
                        <span className="user-info" title={user.email}>
                            {user.name || user.email}
                        </span>
                    )}
                    <span className={`session-timer ${sessionTimeLeft < 60000 ? 'warning' : ''}`} title="Session expires in">
                        {formatTimeLeft(sessionTimeLeft)}
                    </span>
                    <button onClick={() => handleLogout(false)} className="logout-btn">Logout</button>
                </div>
            </header>

            <nav className="admin-nav">
                {/* Hidden tabs - uncomment when needed
                <button
                    className={activeTab === 'services' ? 'active' : ''}
                    onClick={() => setActiveTab('services')}
                >
                    Services
                </button>
                <button
                    className={activeTab === 'events' ? 'active' : ''}
                    onClick={() => setActiveTab('events')}
                >
                    Events
                </button>
                <button
                    className={activeTab === 'blogs' ? 'active' : ''}
                    onClick={() => setActiveTab('blogs')}
                >
                    Blogs
                </button>
                <button
                    className={activeTab === 'volunteers' ? 'active' : ''}
                    onClick={() => setActiveTab('volunteers')}
                >
                    Volunteers
                </button>
                */}
                <button
                    className={activeTab === 'inquiries' ? 'active' : ''}
                    onClick={() => setActiveTab('inquiries')}
                >
                    Inquiries ({inquiries.filter(i => i.status === 'pending').length} new)
                </button>
                <button
                    className={activeTab === 'tiles' ? 'active' : ''}
                    onClick={() => setActiveTab('tiles')}
                >
                    Wall Tiles ({tileReservations.filter(t => t.status === 'pending').length} pending)
                </button>
                <button
                    className={activeTab === 'audit' ? 'active' : ''}
                    onClick={() => setActiveTab('audit')}
                >
                    Audit Trail
                </button>
                <button
                    className={activeTab === 'users' ? 'active' : ''}
                    onClick={() => setActiveTab('users')}
                >
                    Users ({adminUsers.filter(u => u.approval_status === 'pending').length} pending)
                </button>
            </nav>

            <main className="admin-content">
                {activeTab === 'inquiries' && (
                    <div className="admin-section">
                        <div className="section-header">
                            <h2>Tile Inquiries</h2>
                            <div className="tile-stats">
                                <span className="tile-stat">
                                    <strong>{inquiries.filter(i => i.status === 'pending').length}</strong> New
                                </span>
                                <span className="tile-stat">
                                    <strong>{inquiries.filter(i => i.status === 'contacted').length}</strong> Contacted
                                </span>
                                <span className="tile-stat">
                                    <strong>{inquiries.filter(i => i.status === 'confirmed').length}</strong> Confirmed
                                </span>
                            </div>
                        </div>

                        <div className="inquiries-info-banner">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="12" cy="12" r="10"/>
                                <path d="M12 16v-4M12 8h.01"/>
                            </svg>
                            <p>These are tile inquiries from users interested in supporting the mosque. Please contact them to arrange payment and confirm their tiles.</p>
                        </div>

                        <table className="admin-table">
                            <thead>
                                <tr>
                                    <th>Date</th>
                                    <th>Tiles</th>
                                    <th>Donor Details</th>
                                    <th>Payment Plan</th>
                                    <th>Total</th>
                                    <th>Status</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {inquiries.map(inquiry => {
                                    const statusInfo = formatInquiryStatus(inquiry.status);
                                    const tileNums = JSON.parse(inquiry.tile_numbers || '[]');
                                    return (
                                        <tr key={inquiry.inquiry_id} className={inquiry.status === 'pending' ? 'pending-row' : ''}>
                                            <td className="audit-time">
                                                {new Date(inquiry.created_at).toLocaleDateString()}<br/>
                                                <small>{new Date(inquiry.created_at).toLocaleTimeString()}</small>
                                            </td>
                                            <td>
                                                <div className="inquiry-tiles">
                                                    {tileNums.map(num => (
                                                        <span key={num} className="inquiry-tile-badge">#{num}</span>
                                                    ))}
                                                </div>
                                            </td>
                                            <td>
                                                <strong>{inquiry.donor_name}</strong><br/>
                                                <small>{inquiry.donor_email}</small><br/>
                                                <small>{inquiry.donor_phone}</small>
                                                {inquiry.donor_display_name && inquiry.donor_display_name !== inquiry.donor_name && (
                                                    <><br/><small className="display-name">Display: {inquiry.donor_display_name}</small></>
                                                )}
                                            </td>
                                            <td>
                                                <span className="payment-plan-badge">
                                                    {formatPaymentPlan(inquiry.payment_plan)}
                                                </span>
                                                {inquiry.instalment_amount && (
                                                    <><br/><small>${inquiry.instalment_amount.toLocaleString()}/month</small></>
                                                )}
                                            </td>
                                            <td>
                                                <strong>${inquiry.total_amount?.toLocaleString() || '0'} AUD</strong>
                                            </td>
                                            <td>
                                                <span
                                                    className="inquiry-status-badge"
                                                    style={{ backgroundColor: statusInfo.bgColor, color: statusInfo.color }}
                                                >
                                                    {statusInfo.label}
                                                </span>
                                                {inquiry.followed_up_at && (
                                                    <><br/><small>Followed up: {new Date(inquiry.followed_up_at).toLocaleDateString()}</small></>
                                                )}
                                            </td>
                                            <td>
                                                <div className="inquiry-actions">
                                                    {inquiry.status === 'pending' && (
                                                        <button
                                                            onClick={() => updateInquiryStatus(inquiry.inquiry_id, 'contacted')}
                                                            className="edit-btn"
                                                            title="Mark as contacted"
                                                        >
                                                            Mark Contacted
                                                        </button>
                                                    )}
                                                    {(inquiry.status === 'pending' || inquiry.status === 'contacted') && (
                                                        <button
                                                            onClick={() => updateInquiryStatus(inquiry.inquiry_id, 'confirmed')}
                                                            className="approve-btn"
                                                            title="Confirm tiles for this donor"
                                                        >
                                                            Confirm
                                                        </button>
                                                    )}
                                                    {inquiry.status !== 'cancelled' && inquiry.status !== 'confirmed' && (
                                                        <button
                                                            onClick={() => updateInquiryStatus(inquiry.inquiry_id, 'cancelled')}
                                                            className="reject-btn"
                                                            title="Cancel this inquiry"
                                                        >
                                                            Cancel
                                                        </button>
                                                    )}
                                                    <button
                                                        onClick={() => deleteInquiry(inquiry.inquiry_id)}
                                                        className="delete-btn"
                                                        title="Delete this inquiry"
                                                    >
                                                        Delete
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                                {inquiries.length === 0 && (
                                    <tr>
                                        <td colSpan="7" style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                                            No inquiries yet. When users submit tile inquiries from the Wall of Akhirah page, they will appear here.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}

                {activeTab === 'tiles' && (
                    <div className="admin-section">
                        <div className="section-header">
                            <h2>Wall of Akhirah - Tile Reservations</h2>
                            <div className="tile-stats">
                                <span className="tile-stat">
                                    <strong>{tileReservations.filter(t => t.status === 'pending').length}</strong> Pending Verification
                                </span>
                                <span className="tile-stat">
                                    <strong>{tileReservations.filter(t => t.status === 'confirmed').length}</strong> Verified
                                </span>
                                <span className="tile-stat">
                                    <strong>{500 - tileReservations.filter(t => ['pending', 'confirmed'].includes(t.status)).length}</strong> Available
                                </span>
                            </div>
                        </div>

                        {/* Reserved Tiles Section (First 3 rows) */}
                        <div className="reserved-tiles-section">
                            <div className="reserved-tiles-header">
                                <h3>Reserved Tiles (Rows 1-3: Tiles #1-75)</h3>
                                <p>These tiles are reserved for admin allocation only and cannot be purchased by the public.</p>
                                <button
                                    className="add-btn"
                                    onClick={() => setShowReservedForm(!showReservedForm)}
                                >
                                    {showReservedForm ? 'Cancel' : '+ Add Reserved Tile'}
                                </button>
                            </div>

                            {showReservedForm && (
                                <form className="reserved-tile-form" onSubmit={handleAddReservedTile}>
                                    <div className="form-row">
                                        <div className="form-group">
                                            <label>Tile Number (1-75)</label>
                                            <input
                                                type="number"
                                                min="1"
                                                max="75"
                                                value={reservedTileNumber}
                                                onChange={(e) => setReservedTileNumber(e.target.value)}
                                                placeholder="Enter tile number"
                                                required
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>Donor Name</label>
                                            <input
                                                type="text"
                                                value={reservedDonorName}
                                                onChange={(e) => setReservedDonorName(e.target.value)}
                                                placeholder="Enter donor name"
                                                required
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>Email (optional)</label>
                                            <input
                                                type="email"
                                                value={reservedDonorEmail}
                                                onChange={(e) => setReservedDonorEmail(e.target.value)}
                                                placeholder="Enter email"
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>Phone (optional)</label>
                                            <input
                                                type="tel"
                                                value={reservedDonorPhone}
                                                onChange={(e) => setReservedDonorPhone(e.target.value)}
                                                placeholder="Enter phone"
                                            />
                                        </div>
                                    </div>
                                    {reservedFormError && <p className="form-error">{reservedFormError}</p>}
                                    <button type="submit" className="approve-btn">Add Reserved Tile</button>
                                </form>
                            )}
                        </div>

                        <table className="admin-table">
                            <thead>
                                <tr>
                                    <th>Tile #</th>
                                    <th>Donor Name</th>
                                    <th>Email</th>
                                    <th>Phone</th>
                                    <th>Status</th>
                                    <th>Payment</th>
                                    <th>Date</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {tileReservations.map(tile => (
                                    <tr key={tile.tile_number} className={tile.status === 'pending' ? 'pending-row' : ''}>
                                        <td>
                                            <strong>#{tile.tile_number}</strong>
                                            {tile.tile_number <= RESERVED_TILES_COUNT && (
                                                <span className="reserved-badge">Reserved</span>
                                            )}
                                        </td>
                                        <td>{tile.donor_name}</td>
                                        <td>{tile.donor_email}</td>
                                        <td>{tile.donor_phone || '-'}</td>
                                        <td>
                                            <span className={`status-badge status-${tile.status === 'confirmed' ? 'approved' : tile.status}`}>
                                                {tile.status === 'pending' ? 'Pending Verification' : tile.status === 'confirmed' ? 'Verified' : tile.status}
                                            </span>
                                        </td>
                                        <td>
                                            {tile.payment_status === 'paid' ? (
                                                <div className="payment-info">
                                                    <span className="payment-status paid">Paid</span>
                                                    <span className="payment-method">
                                                        {formatPaymentMethod(tile.payment_method_type, tile.payment_method_brand)}
                                                    </span>
                                                </div>
                                            ) : tile.tile_number <= RESERVED_TILES_COUNT ? (
                                                <span className="payment-status admin">Admin</span>
                                            ) : (
                                                <span className="payment-status pending">Pending</span>
                                            )}
                                        </td>
                                        <td>{new Date(tile.created_at).toLocaleDateString()}</td>
                                        <td>
                                            {tile.status === 'pending' && (
                                                <>
                                                    <button
                                                        onClick={() => updateTileStatus(tile.tile_number, 'confirmed')}
                                                        className="approve-btn"
                                                        title="Verify identity and display donor name on tile"
                                                    >
                                                        Verify Identity
                                                    </button>
                                                    <button
                                                        onClick={() => updateTileStatus(tile.tile_number, 'rejected')}
                                                        className="reject-btn"
                                                        title="Reject this tile claim"
                                                    >
                                                        Reject
                                                    </button>
                                                </>
                                            )}
                                            {tile.status === 'confirmed' && (
                                                <button
                                                    onClick={() => updateTileStatus(tile.tile_number, 'pending')}
                                                    className="edit-btn"
                                                    title="Revert to pending verification"
                                                >
                                                    Revert to Pending
                                                </button>
                                            )}
                                            <button onClick={() => deleteTileReservation(tile.tile_number)} className="delete-btn">Delete</button>
                                        </td>
                                    </tr>
                                ))}
                                {tileReservations.length === 0 && (
                                    <tr>
                                        <td colSpan="8" style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                                            No tile reservations yet
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}

                {activeTab === 'audit' && (
                    <div className="admin-section">
                        <div className="section-header">
                            <h2>Audit Trail</h2>
                            <button className="add-btn" onClick={fetchData}>Refresh</button>
                        </div>
                        <table className="admin-table audit-table">
                            <thead>
                                <tr>
                                    <th>Date/Time</th>
                                    <th>User</th>
                                    <th>Action</th>
                                    <th>Type</th>
                                    <th>Details</th>
                                    <th>IP Address</th>
                                    <th>Location</th>
                                </tr>
                            </thead>
                            <tbody>
                                {auditLogs.map(log => {
                                    const actionStyle = formatActionType(log.action_type);
                                    return (
                                        <tr key={log.id} className={log.action_type === 'login_failed' ? 'pending-row' : ''}>
                                            <td className="audit-time">
                                                {new Date(log.created_at).toLocaleString()}
                                            </td>
                                            <td>
                                                <strong>{log.username}</strong>
                                            </td>
                                            <td>{log.action}</td>
                                            <td>
                                                <span
                                                    className="action-type-badge"
                                                    style={{ backgroundColor: actionStyle.color }}
                                                >
                                                    {actionStyle.label}
                                                </span>
                                            </td>
                                            <td className="audit-details">{log.details || '-'}</td>
                                            <td className="audit-ip">{log.ip_address}</td>
                                            <td className="audit-location">{log.location}</td>
                                        </tr>
                                    );
                                })}
                                {auditLogs.length === 0 && (
                                    <tr>
                                        <td colSpan="7" style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                                            No audit logs yet
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}

                {activeTab === 'users' && (
                    <div className="admin-section">
                        <div className="section-header">
                            <h2>Admin Users</h2>
                            <div className="tile-stats">
                                <span className="tile-stat">
                                    <strong>{adminUsers.filter(u => u.approval_status === 'pending').length}</strong> Pending
                                </span>
                                <span className="tile-stat">
                                    <strong>{adminUsers.filter(u => u.approval_status === 'approved').length}</strong> Approved
                                </span>
                                <span className="tile-stat">
                                    <strong>{adminUsers.filter(u => u.approval_status === 'rejected').length}</strong> Rejected
                                </span>
                            </div>
                        </div>

                        <div className="inquiries-info-banner">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="12" cy="12" r="10"/>
                                <path d="M12 16v-4M12 8h.01"/>
                            </svg>
                            <p>Manage admin portal access. Only @pcic.org.au users can sign in. New users require approval before accessing the dashboard.
                            {currentUserIsPrimary && <strong> You are the Primary Admin - you can transfer this role and delete users.</strong>}</p>
                        </div>

                        <table className="admin-table">
                            <thead>
                                <tr>
                                    <th>User</th>
                                    <th>Email</th>
                                    <th>Status</th>
                                    <th>Approved By</th>
                                    <th>Last Login</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {adminUsers.map(adminUser => (
                                    <tr key={adminUser.user_id} className={adminUser.approval_status === 'pending' ? 'pending-row' : ''}>
                                        <td>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                {adminUser.picture && (
                                                    <img
                                                        src={adminUser.picture}
                                                        alt=""
                                                        style={{ width: '32px', height: '32px', borderRadius: '50%' }}
                                                    />
                                                )}
                                                <div>
                                                    <strong>{adminUser.name || 'Unknown'}</strong>
                                                    {adminUser.is_primary_admin === 1 && (
                                                        <span style={{
                                                            marginLeft: '8px',
                                                            padding: '2px 8px',
                                                            background: '#ffc107',
                                                            color: '#000',
                                                            borderRadius: '12px',
                                                            fontSize: '11px',
                                                            fontWeight: 'bold'
                                                        }}>PRIMARY</span>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                        <td>{adminUser.email}</td>
                                        <td>
                                            <span className={`status-badge status-${adminUser.approval_status === 'approved' ? 'approved' : adminUser.approval_status === 'rejected' ? 'rejected' : 'pending'}`}>
                                                {adminUser.approval_status === 'approved' ? 'Approved' :
                                                 adminUser.approval_status === 'rejected' ? 'Rejected' : 'Pending'}
                                            </span>
                                        </td>
                                        <td>
                                            {adminUser.approved_by || '-'}
                                            {adminUser.approved_at && (
                                                <><br/><small>{new Date(adminUser.approved_at).toLocaleDateString()}</small></>
                                            )}
                                        </td>
                                        <td>
                                            {adminUser.last_login ? new Date(adminUser.last_login).toLocaleString() : 'Never'}
                                        </td>
                                        <td>
                                            {adminUser.approval_status === 'pending' && (
                                                <>
                                                    <button
                                                        onClick={() => updateUserApproval(adminUser.user_id, 'approve')}
                                                        className="approve-btn"
                                                    >
                                                        Approve
                                                    </button>
                                                    <button
                                                        onClick={() => updateUserApproval(adminUser.user_id, 'reject')}
                                                        className="reject-btn"
                                                    >
                                                        Reject
                                                    </button>
                                                </>
                                            )}
                                            {adminUser.approval_status === 'approved' && adminUser.email !== user?.email && (
                                                <>
                                                    <button
                                                        onClick={() => updateUserApproval(adminUser.user_id, 'reject')}
                                                        className="reject-btn"
                                                    >
                                                        Revoke
                                                    </button>
                                                    {currentUserIsPrimary && !adminUser.is_primary_admin && (
                                                        <>
                                                            <button
                                                                onClick={() => transferPrimaryAdmin(adminUser.user_id, adminUser.email)}
                                                                className="approve-btn"
                                                                style={{ marginLeft: '5px', background: '#ffc107', color: '#000' }}
                                                            >
                                                                Make Primary
                                                            </button>
                                                            <button
                                                                onClick={() => deleteUser(adminUser.user_id, adminUser.email)}
                                                                className="reject-btn"
                                                                style={{ marginLeft: '5px', background: '#dc3545' }}
                                                            >
                                                                Delete
                                                            </button>
                                                        </>
                                                    )}
                                                </>
                                            )}
                                            {adminUser.approval_status === 'rejected' && (
                                                <>
                                                    <button
                                                        onClick={() => updateUserApproval(adminUser.user_id, 'approve')}
                                                        className="approve-btn"
                                                    >
                                                        Approve
                                                    </button>
                                                    {currentUserIsPrimary && (
                                                        <button
                                                            onClick={() => deleteUser(adminUser.user_id, adminUser.email)}
                                                            className="reject-btn"
                                                            style={{ marginLeft: '5px', background: '#dc3545' }}
                                                        >
                                                            Delete
                                                        </button>
                                                    )}
                                                </>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                                {adminUsers.length === 0 && (
                                    <tr>
                                        <td colSpan="6" style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                                            No users found. Users will appear here after signing in with their @pcic.org.au Google account.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </main>
        </div>
    );
};

export default AdminDashboard;
